import { create } from 'zustand';

/**
 * Events Filter Store - Business logic for events page filtering and view state
 * Manages event filters, view preferences (grid/list), and filter sheet UI
 */
export const useEventsFilterStore = create((set, get) => ({
  // View state
  viewMode: 'grid', // 'grid' | 'list'
  
  // Filter state
  filters: {
    sports: [],
    skillLevels: [],
    dateRange: null, // 'today' | 'this-week' | 'this-weekend' | 'next-week'
    maxDistance: 10, // miles
    timeOfDay: [], // 'morning' | 'afternoon' | 'evening'
  },
  
  // UI state
  isFilterSheetOpen: false,
  
  // Actions - View
  setViewMode: (mode) => set({ viewMode: mode }),
  toggleViewMode: () => set((state) => ({
    viewMode: state.viewMode === 'grid' ? 'list' : 'grid',
  })),
  
  // Actions - Filter Sheet
  openFilterSheet: () => set({ isFilterSheetOpen: true }),
  closeFilterSheet: () => set({ isFilterSheetOpen: false }),
  toggleFilterSheet: () => set((state) => ({
    isFilterSheetOpen: !state.isFilterSheetOpen,
  })),
  
  // Actions - Filters
  toggleSportFilter: (sport) => set((state) => {
    const sports = state.filters.sports.includes(sport)
      ? state.filters.sports.filter((s) => s !== sport)
      : [...state.filters.sports, sport];
    return { filters: { ...state.filters, sports } };
  }),
  
  toggleSkillLevelFilter: (level) => set((state) => {
    const skillLevels = state.filters.skillLevels.includes(level)
      ? state.filters.skillLevels.filter((l) => l !== level)
      : [...state.filters.skillLevels, level];
    return { filters: { ...state.filters, skillLevels } };
  }),
  
  setDateRangeFilter: (range) => set((state) => ({
    filters: { ...state.filters, dateRange: range },
  })),
  
  setMaxDistanceFilter: (distance) => set((state) => ({
    filters: { ...state.filters, maxDistance: distance },
  })),
  
  toggleTimeOfDayFilter: (time) => set((state) => {
    const timeOfDay = state.filters.timeOfDay.includes(time)
      ? state.filters.timeOfDay.filter((t) => t !== time)
      : [...state.filters.timeOfDay, time];
    return { filters: { ...state.filters, timeOfDay } };
  }),
  
  removeFilter: (type, value) => set((state) => {
    switch (type) {
      case 'sport':
        return {
          filters: {
            ...state.filters,
            sports: state.filters.sports.filter((s) => s !== value),
          },
        };
      case 'skillLevel':
        return {
          filters: {
            ...state.filters,
            skillLevels: state.filters.skillLevels.filter((l) => l !== value),
          },
        };
      case 'dateRange':
        return {
          filters: { ...state.filters, dateRange: null },
        };
      case 'timeOfDay':
        return {
          filters: {
            ...state.filters,
            timeOfDay: state.filters.timeOfDay.filter((t) => t !== value),
          },
        };
      case 'maxDistance':
        return {
          filters: { ...state.filters, maxDistance: 10 },
        };
      default:
        return state;
    }
  }),
  
  clearAllFilters: () => set({
    filters: {
      sports: [],
      skillLevels: [],
      dateRange: null,
      maxDistance: 10,
      timeOfDay: [],
    },
  }),
  
  // Getters
  getActiveFilterCount: () => {
    const { filters } = get();
    return (
      filters.sports.length +
      filters.skillLevels.length +
      (filters.dateRange ? 1 : 0) +
      filters.timeOfDay.length +
      (filters.maxDistance !== 10 ? 1 : 0)
    );
  },
  
  hasActiveFilters: () => get().getActiveFilterCount() > 0,
}));

export default useEventsFilterStore;
